import * as v from 'valibot';

import { ESeverity } from '#shared';

import { CLINICAL_EVENTS_DISPLAY_OPTIONS } from '../model';

export type ControlKind =
  'severity' | 'ageSteps' | 'display' | 'windowMultiplier';

export interface ValueContract {
  schema: v.GenericSchema;
  rangeText: string;
  control: ControlKind;
  options: readonly string[];
}

export const EVENT_SEVERITY_PREFIX = 'eventSeverity.';

const SEVERITY_OPTIONS: readonly string[] = Object.values(ESeverity);

const severityContract: ValueContract = {
  schema: v.enum(ESeverity),
  rangeText: `One of ${SEVERITY_OPTIONS.join(', ')}`,
  control: 'severity',
  options: SEVERITY_OPTIONS,
};

const minutes = v.pipe(v.number(), v.integer(), v.minValue(1));

const ageStepsContract: ValueContract = {
  schema: v.pipe(
    v.object({
      halfFromMinutes: minutes,
      threeQuartersFromMinutes: minutes,
      fullFromMinutes: minutes,
    }),
    v.check(
      ({ halfFromMinutes, threeQuartersFromMinutes, fullFromMinutes }) =>
        halfFromMinutes < threeQuartersFromMinutes &&
        threeQuartersFromMinutes < fullFromMinutes,
    ),
  ),
  rangeText:
    'Three whole numbers of minutes, at least 1 and strictly increasing: half, three quarters, full',
  control: 'ageSteps',
  options: [],
};

export const APPROVAL_AGE_STEPS_KEY = 'caseTimeline.approvalAgeSteps';

const displayContract: ValueContract = {
  schema: v.picklist(CLINICAL_EVENTS_DISPLAY_OPTIONS),
  rangeText: `One of ${CLINICAL_EVENTS_DISPLAY_OPTIONS.join(', ')}`,
  control: 'display',
  options: CLINICAL_EVENTS_DISPLAY_OPTIONS,
};

export const CLINICAL_EVENTS_DISPLAY_KEY = 'caseTimeline.clinicalEventsDisplay';

export const EVENT_CARD_WINDOW_MULTIPLIER_KEY = 'eventCards.windowMultiplier';

export const WINDOW_MULTIPLIER_STEPS: readonly number[] = Array.from(
  { length: 16 },
  (_, index) => (index + 1) * 0.25,
);

const windowMultiplierContract: ValueContract = {
  schema: v.picklist(WINDOW_MULTIPLIER_STEPS as [number, ...number[]]),
  rangeText: 'A multiple of 0.25 from 0.25 to 4',
  control: 'windowMultiplier',
  options: [],
};

const CONTRACTS: readonly { pattern: RegExp; contract: ValueContract }[] = [
  {
    pattern: /^eventSeverity\.[A-Za-z][A-Za-z0-9]*$/,
    contract: severityContract,
  },
  {
    pattern: /^caseTimeline\.approvalAgeSteps$/,
    contract: ageStepsContract,
  },
  {
    pattern: /^caseTimeline\.clinicalEventsDisplay$/,
    contract: displayContract,
  },
  {
    pattern: /^eventCards\.windowMultiplier$/,
    contract: windowMultiplierContract,
  },
];

export const contractFor = (key: string): ValueContract | undefined =>
  CONTRACTS.find(entry => entry.pattern.test(key))?.contract;

export const checkValue = (
  key: string,
  value: unknown,
): 'ok' | 'drift' | 'unmirrored' => {
  const contract = contractFor(key);
  if (!contract) {
    return 'unmirrored';
  }
  return v.is(contract.schema, value) ? 'ok' : 'drift';
};
